/**
 * Game_Demo_2 – Platformer with physics, character model, animations, spiral platforms.
 *
 * Modules used: Scene_Module (skybox, particles, clouds, mountains, floor, platforms), Physics_Module (world, floor body,
 * platform bodies, player body), Lighting_Module, Movement_Module (MovementType.Physics + body), Input_Module,
 * Camera_Module (third person, inputProvider), Asset_loading (character GLB), Animation_Module (walk/run/jump/idle),
 * UI_Module (movement joystick).
 *
 * Flow: init() → setupScene/Camera/Renderer → setupHandlers (SceneHandler, PhysicsHandler, InputManager, AssetManager)
 * → setupSkyboxAndAtmosphere, setupLighting, setupPhysics, setupFloor → generateSpiralPlatforms, createTopCenterPlatform
 * → loadCharacter (GLB) → createPlayer (mesh + body + MovementManager) → setupThirdPersonCamera, setupEventListeners
 * → createTimer/ScoreDisplay/StartMessage, setupMobileJoystick → start() = requestAnimationFrame(loop).
 * Score: climb height. Game over: time runs out. Timer counts down.
 */

import "./style.css";
import * as THREE from "three";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { SceneHandler } from "./modules/Scene_Module/index.js";
import { PhysicsHandler, PHYSICS_BODY_TYPES } from "./modules/Physics_Module/index.js";
import { LightingModule } from "./modules/Lighting_Module/index.js";
import { MovementManager, MovementType } from "./modules/Movement_Module/index.js";
import { InputManager, KeyCode } from "./modules/Input_Module/index.js";
import { CameraManager } from "./modules/Camera_Module/index.js";
import { AssetManager } from "./modules/Asset_loading/index.js";
import { UIManager, type IJoystick } from "./modules/UI_Module/index.js";
import { AnimationManager } from "./modules/Animation_Module/index.js";

class Game {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private canvas!: HTMLCanvasElement;
  private cameraManager!: CameraManager;
  private sceneHandler!: SceneHandler;
  private physicsHandler!: PhysicsHandler;
  private lightingHandler!: LightingModule;
  private skyboxMesh!: THREE.Mesh;
  private directionalLight!: THREE.DirectionalLight;
  private hemisphereLight!: THREE.HemisphereLight;
  private ambientLight!: THREE.AmbientLight;
  private fog!: THREE.FogExp2;
  private baseRoughnessMap!: THREE.Texture;
  private floorRoughnessMap!: THREE.Texture;
  private platformRoughnessMap!: THREE.Texture;
  private platformDiffuseMap!: THREE.Texture;
  private inputHandler!: InputManager;
  private assetManager!: AssetManager;
  private animManager!: AnimationManager | null;
  private player!: {
    mesh: THREE.Mesh | THREE.Group | THREE.Object3D;
    bodyId: string;
    movement: MovementManager;
    canJump: boolean;
    isGrounded: boolean;
    isJumping: boolean;
    wasInAir: boolean;
    hasWon: boolean;
    spaceWasReleased: boolean;
    positionOffset: [number, number, number]; // Offset to keep physics body center at bounding box center
  };
  private physicsWorld!: any;
  private worldId: string = "main-world";
  private floorMesh!: any;
  private platforms: Array<{
    mesh: any;
    bodyId: string;
    originalPosition: [number, number, number];
    previousPosition: [number, number, number];
    isMoving: boolean;
    movementDirection: [number, number, number];
    movementAmplitude: number;
    movementSpeed: number;
    movementPhase: number;
    size: number;
  }> = [];
  private animationId?: number;
  private boundResizeHandler!: () => void;
  private time: number = 0;
  private lastTime: number = 0;
  private timeRemaining: number = 60; // Countdown from 60 seconds
  private static readonly TIMER_START = 60;
  private gameEnded: boolean = false;
  private gameOverReason: string = "";
  private timerElement!: HTMLDivElement;
  private startMessageElement!: HTMLDivElement;
  private showStartMessage: boolean = true;
  private uiManager!: UIManager;
  private moveJoystick: IJoystick | null = null;

  // Score-based endless mode
  private score: number = 0;
  private maxHeightReached: number = 0;
  private nextPlatformIndex: number = 0;
  private scoreElement!: HTMLDivElement;
  private static readonly INITIAL_PLATFORM_COUNT = 50;
  private static readonly PLATFORM_SPAWN_AHEAD = 25;
  private static readonly PLATFORM_REMOVE_BELOW = 55;

  // Physics constants
  private readonly GRAVITY = -40;
  private readonly JUMP_FORCE = 22;
  private readonly MOVE_SPEED = 12;
  private readonly SPRINT_MULTIPLIER = 1.5;

  // Animation speed scaling
  // Base animation speed is designed for MOVE_SPEED of 8.5
  // Scale animations proportionally to current speed
  private readonly BASE_ANIMATION_SPEED = 8.5;
  private readonly WALK_ANIMATION_SCALE = this.MOVE_SPEED / this.BASE_ANIMATION_SPEED;
  private readonly RUN_ANIMATION_SCALE = (this.MOVE_SPEED * this.SPRINT_MULTIPLIER) / this.BASE_ANIMATION_SPEED;
  // 0 = current textures, 1 = alternate textures from gameAssets.json (arrays)
  private readonly ASSET_VARIANT_INDEX = 0;

  /** Asset paths from public/gameAssets.json (ground, sky, platform, player). */
  private gameAssets!: Record<string, string | string[]>;
  /** Base URL for relative asset paths (e.g. /p/jump_away_3d/ or ./). */
  private assetBaseUrl: string = "./";
  /** Colors from public/gameTheme.json (fog, lights, platforms, UI). */
  private gameTheme!: Record<string, string | string[]>;

  constructor() {
    this.init();
  }
  /** Parse hex string from gameTheme to number (e.g. "f0d2b2" → 0xf0d2b2). */
  private themeColor(key: string): number {
    const v = this.gameTheme[key];
    return typeof v === "string" ? parseInt(v, 16) : 0xffffff;
  }
  /** Resolve asset path; supports string or array in gameAssets.json. */
  private assetPath(key: string, variantIndex: number = 0): string {
    const entry = this.gameAssets[key];
    if (Array.isArray(entry)) {
      return entry[variantIndex] ?? entry[0] ?? "";
    }
    return entry ?? "";
  }
  /** URL for loading: use as-is if absolute (CDN), else base + path for relative. */
  private assetUrl(key: string, variantIndex: number = 0): string {
    const path = this.assetPath(key, variantIndex);
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base = this.assetBaseUrl;
    return base.endsWith("/") ? base + path : base + "/" + path;
  }

  /** Apply gameTheme to CSS variables for UI (score, game over, win screen, etc.). */
  private applyThemeToCSS(): void {
    const root = document.documentElement.style;
    if (this.gameTheme.uiRed) root.setProperty("--ui-red", "#" + this.gameTheme.uiRed);
    if (this.gameTheme.uiRedDark) root.setProperty("--ui-red-dark", "#" + this.gameTheme.uiRedDark);
    if (this.gameTheme.uiRedLight) root.setProperty("--ui-red-light", "#" + this.gameTheme.uiRedLight);
    if (this.gameTheme.uiBlack) root.setProperty("--ui-black", "#" + this.gameTheme.uiBlack);
    if (this.gameTheme.uiWhite) root.setProperty("--ui-white", "#" + this.gameTheme.uiWhite);
    if (this.gameTheme.winTitle) root.setProperty("--win-title", "#" + this.gameTheme.winTitle);
  }

  /** Init order: load gameAssets + gameTheme → scene → camera → renderer → handlers → skybox/lighting → physics → floor → platforms → character → player → camera → UI → start. */
  private async init(): Promise<void> {
    const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "./";
    this.assetBaseUrl = base;
    const [assetsRes, themeRes] = await Promise.all([fetch(`${base}gameAssets.json`), fetch(`${base}gameTheme.json`)]);
    this.gameAssets = await assetsRes.json();
    this.gameTheme = await themeRes.json();
    this.applyThemeToCSS();
    this.setupScene();
    this.setupCamera();
    this.setupRenderer();
    this.setupHandlers();
    this.setupSkyboxAndAtmosphere();
    this.setupLighting();
    this.setupPhysics();
    this.setupFloor();
    this.generateSpiralPlatforms();
    await this.loadCharacter();
    this.createPlayer();
    this.setupThirdPersonCamera();
    this.setupEventListeners();
    this.createTimer();
    this.createScoreDisplay();
    this.createStartMessage();
    this.setupMobileJoystick();
    this.start();
  }
  /** Scene only; no fog here (fog set in setupSkyboxAndAtmosphere). */
  private setupScene(): void { this.scene = new THREE.Scene(); }
  /** PerspectiveCamera; position and lookAt set before player exists. */
  private setupCamera(): void {
    this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.camera.position.set(0, 50, 150);
    this.camera.lookAt(0, 0, 0);
  }
  /** WebGLRenderer, canvas, size, tone mapping, color space. */
  private setupRenderer(): void {
    this.canvas = document.querySelector("#gameCanvas") as HTMLCanvasElement;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }
  /** CameraManager + ThirdPersonCamera; inputProvider = InputManager, target = player.mesh. */
  private setupThirdPersonCamera(): void {
    this.cameraManager = new CameraManager();
    this.cameraManager.createThirdPersonCamera(this.camera, {
      distance: 15,
      minDistance: 5,
      maxDistance: 30,
      heightOffset: 5,
      mouseSensitivity: 0.003,
      followTargetRotation: false,
      enableZoom: true,
      zoomSpeed: 1.0,
      smoothing: true,
      smoothFactor: 0.1,
      initialYaw: 0,
      initialPitch: THREE.MathUtils.degToRad(17),
      minPitch: THREE.MathUtils.degToRad(-20),
      maxPitch: THREE.MathUtils.degToRad(80),
      inputProvider: this.inputHandler,
    });
    this.cameraManager.setTarget(this.player.mesh);
  }
  /** SceneHandler, LightingModule, PhysicsHandler, InputManager (joystick config), AssetManager; inputHandler.connect(). */
  private setupHandlers(): void {
    this.sceneHandler = new SceneHandler(this.scene);
    this.lightingHandler = new LightingModule(this.scene as any);
    this.physicsHandler = new PhysicsHandler();

    // Initialize InputManager with joystick config
    this.inputHandler = new InputManager({
      joysticks: [
        {
          id: 'movement',
          zone: 'bottom-left',
          zoneRadius: 100,
          maxDistance: 60,
          deadzone: 0.15
        }
      ]
    });

    this.assetManager = new AssetManager({
      baseUrl: "",
    });
    this.animManager = null;
    this.inputHandler.connect();
  }
  /** Gradient skybox, fog, atmospheric particles, volumetric clouds, distant mountains (SceneHandler). */
  private setupSkyboxAndAtmosphere(): void {
    const skyGeometry = new THREE.SphereGeometry(520, 48, 32);
    const skyMaterial = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    const skyTextureUrl = this.assetUrl("sky", this.ASSET_VARIANT_INDEX);
    const skyTexture = new THREE.TextureLoader().load(skyTextureUrl, (texture) => {
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      skyMaterial.map = texture;
      skyMaterial.needsUpdate = true;
    });
    skyMaterial.map = skyTexture;
    this.skyboxMesh = new THREE.Mesh(skyGeometry, skyMaterial);
    this.skyboxMesh.name = "textured-skybox";
    this.skyboxMesh.frustumCulled = false;
    this.skyboxMesh.renderOrder = -1;
    this.scene.add(this.skyboxMesh);
    this.fog = new THREE.FogExp2(this.themeColor("fog"), 0.009);
    this.scene.fog = this.fog;
    this.sceneHandler.createAtmosphericParticles({
      count: 700,
      minRadius: 90,
      maxRadius: 240,
      minHeight: 35,
      size: 1.2,
      opacity: 0.5,
      colorHue: 0.58,
      colorSaturation: 0.25,
    });
    this.sceneHandler.createVolumetricClouds({
      count: 32,
      minHeight: 45,
      maxHeight: 160,
      spread: 230,
    });
    this.sceneHandler.createDistantMountains({
      count: 10,
      minDistance: 190,
      maxDistance: 260,
      minHeight: 35,
      maxHeight: 80,
    });
  }
  /** Renderer shadows; hemisphere, ambient, directional, point lights (LightingModule). */
  private setupLighting(): void {
    this.lightingHandler.configureRendererShadows(this.renderer as any, {
      enabled: true,
      type: THREE.PCFSoftShadowMap,
      autoUpdate: true,
    });
    const hemisphereLightId = this.lightingHandler.createHemisphereLight({
      skyColor: this.themeColor("hemisphereSky"),
      groundColor: this.themeColor("hemisphereGround"),
      intensity: 0.55,
      position: [0, 50, 0],
    });
    const hemisphereLight = this.lightingHandler.getLight(hemisphereLightId);
    if (hemisphereLight instanceof THREE.HemisphereLight) {
      this.hemisphereLight = hemisphereLight;
    }

    const ambientLightId = this.lightingHandler.createAmbientLight({
      color: this.themeColor("ambient"),
      intensity: 0.25,
    });
    const ambientLight = this.lightingHandler.getLight(ambientLightId);
    if (ambientLight instanceof THREE.AmbientLight) {
      this.ambientLight = ambientLight;
    }

    const directionalLightId = this.lightingHandler.createDirectionalLight({
      color: this.themeColor("directionalMain"),
      intensity: 1.05,
      position: [70, 140, 60],
      castShadow: true,
      shadowMapQuality: "high",
      shadowCamera: {
        left: -120,
        right: 120,
        top: 120,
        bottom: -120,
        near: 0.1,
        far: 280,
      },
      shadowBias: -0.0001,
      shadowNormalBias: 0.02,
      shadowRadius: 4,
    });
    const directionalLight = this.lightingHandler.getLight(directionalLightId);
    if (directionalLight instanceof THREE.DirectionalLight) {
      this.directionalLight = directionalLight;
    }
    this.lightingHandler.createDirectionalLight({
      color: this.themeColor("directionalFill"),
      intensity: 0.22,
      position: [-40, 60, -40],
      castShadow: false,
    });
    this.lightingHandler.createDirectionalLight({
      color: this.themeColor("directionalRim"),
      intensity: 0.45,
      position: [-90, 80, -120],
      castShadow: false,
    });
    this.lightingHandler.createPointLight({
      color: this.themeColor("pointLight"),
      intensity: 0.45,
      position: [0, 12, 0],
      distance: 35,
      decay: 2,
      castShadow: false,
    });
  }
  /** Subtle noise map for roughness variation on large surfaces. */
  private createSurfaceRoughnessTexture(size: number = 128): THREE.Texture {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return new THREE.Texture();
    }

    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;
    for (let i = 0; i < size * size; i++) {
      const shade = 200 + Math.random() * 55; // 200-255
      const idx = i * 4;
      data[idx] = shade;
      data[idx + 1] = shade;
      data[idx + 2] = shade;
      data[idx + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.NoColorSpace;
    texture.needsUpdate = true;
    if (this.renderer) {
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    }
    return texture;
  }
  /** Create physics world (gravity, worldId). */
  private setupPhysics(): void {
    this.physicsWorld = this.physicsHandler.createWorld({ gravity: [0, this.GRAVITY, 0], worldId: this.worldId });
  }
  /** Floor mesh (PlaneGeometry) + static box body; syncMeshWithBody. */
  private setupFloor(): void {
    if (!this.baseRoughnessMap) {
      this.baseRoughnessMap = this.createSurfaceRoughnessTexture(128);
      this.floorRoughnessMap = this.baseRoughnessMap.clone();
      this.floorRoughnessMap.repeat.set(6, 6);
      this.floorRoughnessMap.needsUpdate = true;
      this.platformRoughnessMap = this.baseRoughnessMap.clone();
      this.platformRoughnessMap.repeat.set(3, 3);
      this.platformRoughnessMap.needsUpdate = true;
    }
    const textureLoader = new THREE.TextureLoader();
    if (!this.platformDiffuseMap) {
      const platformTextureUrl = this.assetUrl("platform", this.ASSET_VARIANT_INDEX);
      this.platformDiffuseMap = textureLoader.load(platformTextureUrl);
      this.platformDiffuseMap.wrapS = THREE.RepeatWrapping;
      this.platformDiffuseMap.wrapT = THREE.RepeatWrapping;
      this.platformDiffuseMap.repeat.set(2, 2);
      this.platformDiffuseMap.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      this.platformDiffuseMap.colorSpace = THREE.SRGBColorSpace;
    }
    const floorGeometry = new THREE.PlaneGeometry(100, 100);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: this.themeColor("floorMaterial"),
      metalness: 0.05,
      roughness: 0.9,
      roughnessMap: this.floorRoughnessMap,
    });
    const groundTextureUrl = this.assetUrl("ground", this.ASSET_VARIANT_INDEX);
    textureLoader.load(groundTextureUrl, (texture) => {
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(4, 4);
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      texture.colorSpace = THREE.SRGBColorSpace;
      floorMaterial.map = texture;
      floorMaterial.needsUpdate = true;
    });
    this.floorMesh = this.sceneHandler.createMesh(
      floorGeometry as any,
      floorMaterial as any,
      {
        name: "floor",
        receiveShadow: true,
        castShadow: false,
        normalize: false,
      }
    );
    // Rotate floor mesh -90 degrees on X axis to make it horizontal (PlaneGeometry is vertical by default)
    this.floorMesh.rotation.set(-Math.PI / 2, 0, 0);
    this.scene.add(this.floorMesh);
    const grid = new THREE.GridHelper(100, 40, this.themeColor("gridLine"), this.themeColor("gridLine"));
    const gridMaterial = grid.material;
    if (Array.isArray(gridMaterial)) {
      gridMaterial.forEach((mat) => {
        mat.transparent = true;
        mat.opacity = 0.12;
      });
    } else {
      gridMaterial.transparent = true;
      gridMaterial.opacity = 0.12;
    }
    grid.position.y = 0.02;
    this.scene.add(grid);

    // Create finite static ground body (box shape, 100x100 units, thin)
    // After rotation, box extends from -0.1 to +0.1 in Y (thickness)
    // Position at y = -0.1 so top surface is at y = 0 (matching visual plane)
    // halfExtents: [50, 0.1, 50] = 100x0.2x100 units (width x thickness x depth)
    this.physicsHandler.createBoxBody(this.worldId, {
      bodyId: "floor",
      position: [0, 0, 0], // Position so top surface is at y = 0
      rotation: [0, 0, 0], // Rotate to be horizontal
      type: PHYSICS_BODY_TYPES.STATIC,
      halfExtents: [50, 0.1, 50], // 100x0.2x100 units (width x thickness x depth)
    });

    // Sync only rotation, not position (mesh stays at y=0, body at y=-0.1)
    this.physicsHandler.syncMeshWithBody(
      this.worldId,
      "floor",
      this.floorMesh,
      {
        syncPosition: true, // Don't sync position - mesh stays at [0,0,0]
        syncRotation: false,  // Sync rotation to match body
      }
    );
  }
  /** Spiral platform constants (slightly tighter spacing). */
  private static readonly PLATFORM_HEIGHT = 0.5;
  private static readonly PLATFORMS_PER_TURN = 8;
  private static readonly VERTICAL_STEP = 3.2; // Slightly less than 4 for easier climbing
  private static readonly START_HEIGHT = 5;
  private static readonly MIN_SIZE = 4;
  private static readonly MAX_SIZE = 15;
  private static readonly MAX_HORIZONTAL_PLATFORM_DISTANCE = 9;

  private getPlatformColor(index: number): number {
    const arr = this.gameTheme.platformColors as string[] | undefined;
    if (!arr || !arr.length) return 0x4ecdc4;
    return parseInt(arr[index % arr.length], 16);
  }

  private getHighestPlatformY(): number {
    if (this.platforms.length === 0) return Game.START_HEIGHT - Game.VERTICAL_STEP;
    return Math.max(...this.platforms.map((p) => p.originalPosition[1]));
  }

  /** Initial spiral of platforms (unlimited mode: only first batch). */
  private generateSpiralPlatforms(): void {
    const angleBetweenPlatforms = (Math.PI * 2) / Game.PLATFORMS_PER_TURN;
    const maxRadiusForJump =
      Game.MAX_HORIZONTAL_PLATFORM_DISTANCE / (0.8 * Math.sin(angleBetweenPlatforms / 2));
    const minRadius = Math.min(14, maxRadiusForJump * 0.5);
    const maxRadius = Math.min(36, maxRadiusForJump);

    for (let i = 0; i < Game.INITIAL_PLATFORM_COUNT; i++) {
      const actualY = Game.START_HEIGHT + i * Game.VERTICAL_STEP;
      const t = i / Math.max(1, Game.INITIAL_PLATFORM_COUNT - 1);
      const platformSize = Game.MAX_SIZE - (Game.MAX_SIZE - Game.MIN_SIZE) * t;
      const radius = maxRadius - (maxRadius - minRadius) * t;
      const angle = (i / Game.PLATFORMS_PER_TURN) * Math.PI * 2;
      const x = radius * Math.cos(angle);
      const z = radius * Math.sin(angle);
      const position: [number, number, number] = [x, actualY, z];
      const bodyId = `platform-${i}`;
      const color = this.getPlatformColor(i);
      const platformGeometry = new THREE.BoxGeometry(
        platformSize,
        Game.PLATFORM_HEIGHT,
        platformSize
      );
      const platformMaterial = new THREE.MeshStandardMaterial({
        color,
        metalness: 0.3,
        roughness: 0.7,
        roughnessMap: this.platformRoughnessMap,
        map: this.platformDiffuseMap,
        emissive: color,
        emissiveIntensity: 0.15,
      });
      const platformMesh = this.sceneHandler.createMesh(
        platformGeometry as any,
        platformMaterial as any,
        { name: bodyId, receiveShadow: true, castShadow: false, normalize: false }
      );
      const edgeColor = new THREE.Color(color).lerp(new THREE.Color(this.themeColor("platformEdgeLerp")), 0.6);
      const edgeMaterial = new THREE.LineBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0.45,
      });
      const edgeLines = new THREE.LineSegments(new THREE.EdgesGeometry(platformGeometry, 35), edgeMaterial);
      edgeLines.renderOrder = 2;
      platformMesh.add(edgeLines);
      platformMesh.position.set(...position);
      this.scene.add(platformMesh as any);
      const isMoving = i % 3 === 0;
      let movementDirection: [number, number, number] = [0, 0, 0];
      let movementAmplitude = 0;
      let movementSpeed = 0;
      let movementPhase = 0;
      if (isMoving) {
        const tangentX = -Math.sin(angle);
        const tangentZ = Math.cos(angle);
        movementDirection = i % 6 === 0 ? [tangentX, 0, tangentZ] : [0, 1, 0];
        movementAmplitude = 3;
        movementSpeed = 1.5;
        movementPhase = i * 0.5;
      }
      this.physicsHandler.createBoxBody(this.worldId, {
        bodyId,
        type: isMoving ? PHYSICS_BODY_TYPES.KINEMATIC : PHYSICS_BODY_TYPES.STATIC,
        position,
        halfExtents: [platformSize / 2, Game.PLATFORM_HEIGHT / 2, platformSize / 2],
      });
      this.physicsHandler.syncMeshWithBody(this.worldId, bodyId, platformMesh, {
        syncPosition: true,
        syncRotation: true,
      });
      this.platforms.push({
        mesh: platformMesh,
        bodyId,
        originalPosition: [...position],
        previousPosition: [...position],
        isMoving,
        movementDirection,
        movementAmplitude,
        movementSpeed,
        movementPhase,
        size: platformSize,
      });
    }
    this.nextPlatformIndex = Game.INITIAL_PLATFORM_COUNT;
  }

  /** Spawn more platforms above current highest (unlimited mode). */
  private spawnMorePlatforms(count: number): void {
    const highestY = this.getHighestPlatformY();
    const angleBetweenPlatforms = (Math.PI * 2) / Game.PLATFORMS_PER_TURN;
    const maxRadiusForJump =
      Game.MAX_HORIZONTAL_PLATFORM_DISTANCE / (0.8 * Math.sin(angleBetweenPlatforms / 2));
    const minRadius = Math.min(14, maxRadiusForJump * 0.5);
    const maxRadius = Math.min(36, maxRadiusForJump);

    for (let k = 0; k < count; k++) {
      const i = this.nextPlatformIndex + k;
      const actualY = highestY + (k + 1) * Game.VERTICAL_STEP;
      const t = i / Math.max(1, i);
      const platformSize = Game.MAX_SIZE - (Game.MAX_SIZE - Game.MIN_SIZE) * Math.min(t, 1);
      const radius = maxRadius - (maxRadius - minRadius) * Math.min(t, 1);
      const angle = (i / Game.PLATFORMS_PER_TURN) * Math.PI * 2;
      const x = radius * Math.cos(angle);
      const z = radius * Math.sin(angle);
      const position: [number, number, number] = [x, actualY, z];
      const bodyId = `platform-${i}`;
      const color = this.getPlatformColor(i);
      const platformGeometry = new THREE.BoxGeometry(
        platformSize,
        Game.PLATFORM_HEIGHT,
        platformSize
      );
      const platformMaterial = new THREE.MeshStandardMaterial({
        color,
        metalness: 0.3,
        roughness: 0.7,
        roughnessMap: this.platformRoughnessMap,
        map: this.platformDiffuseMap,
        emissive: color,
        emissiveIntensity: 0.15,
      });
      const platformMesh = this.sceneHandler.createMesh(
        platformGeometry as any,
        platformMaterial as any,
        { name: bodyId, receiveShadow: true, castShadow: false, normalize: false }
      );
      const edgeColor = new THREE.Color(color).lerp(new THREE.Color(this.themeColor("platformEdgeLerp")), 0.6);
      const edgeMaterial = new THREE.LineBasicMaterial({
        color: edgeColor,
        transparent: true,
        opacity: 0.45,
      });
      const edgeLines = new THREE.LineSegments(new THREE.EdgesGeometry(platformGeometry, 35), edgeMaterial);
      edgeLines.renderOrder = 2;
      platformMesh.add(edgeLines);
      platformMesh.position.set(...position);
      this.scene.add(platformMesh as any);
      const isMoving = i % 3 === 0;
      let movementDirection: [number, number, number] = [0, 0, 0];
      let movementAmplitude = 0;
      let movementSpeed = 0;
      let movementPhase = 0;
      if (isMoving) {
        const tangentX = -Math.sin(angle);
        const tangentZ = Math.cos(angle);
        movementDirection = i % 6 === 0 ? [tangentX, 0, tangentZ] : [0, 1, 0];
        movementAmplitude = 3;
        movementSpeed = 1.5;
        movementPhase = i * 0.5;
      }
      this.physicsHandler.createBoxBody(this.worldId, {
        bodyId,
        type: isMoving ? PHYSICS_BODY_TYPES.KINEMATIC : PHYSICS_BODY_TYPES.STATIC,
        position,
        halfExtents: [platformSize / 2, Game.PLATFORM_HEIGHT / 2, platformSize / 2],
      });
      this.physicsHandler.syncMeshWithBody(this.worldId, bodyId, platformMesh, {
        syncPosition: true,
        syncRotation: true,
      });
      this.platforms.push({
        mesh: platformMesh,
        bodyId,
        originalPosition: [...position],
        previousPosition: [...position],
        isMoving,
        movementDirection,
        movementAmplitude,
        movementSpeed,
        movementPhase,
        size: platformSize,
      });
    }
    this.nextPlatformIndex += count;
  }

  /** Remove platforms far below player to keep body count bounded. */
  private removePlatformsBelowY(belowY: number): void {
    const toRemove = this.platforms.filter((p) => p.originalPosition[1] < belowY);
    for (const p of toRemove) {
      this.scene.remove(p.mesh as THREE.Object3D);
      this.physicsHandler.deleteBody(this.worldId, p.bodyId, false);
    }
    if (toRemove.length > 0) {
      this.platforms = this.platforms.filter((plat) => plat.originalPosition[1] >= belowY);
    }
  }
  /** Load character GLB via AssetManager; clone for player; init AnimationManager (walk, run, jump, idle). */
  private async loadCharacter(): Promise<void> {
    try {
      const characterAsset = await this.assetManager.loadAsset({
        url: this.assetUrl("player"),
        type: "model",
        model: {
          scale: 1,
          castShadow: true,
          receiveShadow: true,
        },
      });

      const model = characterAsset.asset as THREE.Group;

      this.sceneHandler.normalizeModel(model, {
        normalizeGeometryScale: 1, // Scale to 1.4 units (height)
        normalizePosition: true,
        normalizeRotation: true
      });

      (this as any).characterModel = model;
    } catch (error) {
      (this as any).characterModel = null;
    }
  }
  /** Player: cloned character mesh, capsule body, MovementManager(Physics). */
  private createPlayer(): void {
    const playerName = "player";
    let playerMesh: THREE.Mesh | THREE.Group | THREE.Object3D;
    const characterModel = (this as any).characterModel as THREE.Group | null;
    if (characterModel) {
      // Clone the normalized model for the player instance
      playerMesh = clone(characterModel);
      playerMesh.name = playerName;

      // Ensure cloned model's scale is preserved
      playerMesh.scale.copy(characterModel.scale);
      playerMesh.updateMatrixWorld(true);

      // Set shadows (model already has shadows from AssetManager, but ensure they're set)
      this.sceneHandler.setCastReceiveShadows(
        playerMesh as any,
        true,
        true,
        true
      );

      // Preserve animations from the original model
      const originalAnimations = (characterModel as any).animations as THREE.AnimationClip[];
      if (originalAnimations && originalAnimations.length > 0) {
        (playerMesh as any).animations = originalAnimations;
      }
    } else {
      const playerGeometry = new THREE.CapsuleGeometry(0.5, 1.0);
      const playerMaterial = new THREE.MeshStandardMaterial({
        color: this.themeColor("playerFallback"),
        metalness: 0.3,
        roughness: 0.7,
      });
      playerMesh = this.sceneHandler.createMesh(
        playerGeometry as any,
        playerMaterial as any,
        {
          name: playerName,
          receiveShadow: true,
          castShadow: true,
        }
      ) as any;
    }
    // Set initial mesh position to spawn above ground
    playerMesh.position.set(0, 0, 0);
    playerMesh.rotation.set(0, 0, 0);
    playerMesh.updateMatrixWorld(true);

    this.scene.add(playerMesh as any);

    playerMesh.updateMatrixWorld(true);

    // Compute bounding box for physics body and mesh alignment
    const localBbox = new THREE.Box3();
    localBbox.setFromObject(playerMesh);
    const localBboxCenter = localBbox.getCenter(new THREE.Vector3());
    const localBboxMin = localBbox.min.clone();
    const bboxSize = localBbox.getSize(new THREE.Vector3());

    const cylinderHeight = bboxSize.y;
    const worldBbox = new THREE.Box3();
    worldBbox.setFromObject(playerMesh);
    const worldBboxCenter = worldBbox.getCenter(new THREE.Vector3());

    // Body center Y so cylinder BOTTOM (feet) is at safe spawn height
    const minFeetY = cylinderHeight / 2 + 0.5;
    const spawnBodyCenterY = Math.max(worldBboxCenter.y, minFeetY);

    this.physicsHandler.createCylinderBody(this.worldId, {
      bodyId: playerName,
      position: [worldBboxCenter.x, spawnBodyCenterY, worldBboxCenter.z],
      radiusTop: 0.5,
      radiusBottom: 0.5,
      height: cylinderHeight,
      mass: 70,
      friction: 0.0,
      restitution: 0.0,
      linearDamping: 0.1,
      angularDamping: 1.0,
      allowSleep: false,
    });
    const playerBody = this.physicsHandler.getBody(this.worldId, playerName);
    if (!playerBody) return;
    playerBody.fixedRotation = true;
    playerBody.updateMassProperties();

    // Offset so mesh FEET (local bbox min Y) align with cylinder BOTTOM: mesh.position.y + localBboxMin.y = body.y - height/2
    const positionOffset: [number, number, number] = [
      -localBboxCenter.x,
      -(cylinderHeight / 2 + localBboxMin.y),
      -localBboxCenter.z
    ];

    // Sync mesh with physics body using position offset
    this.physicsHandler.syncMeshWithBody(
      this.worldId,
      playerName,
      playerMesh as any,
      {
        syncPosition: true,
        syncRotation: true,
        positionOffset: positionOffset,
      }
    );
    const movement = new MovementManager(
      playerMesh as any,
      undefined,  // No scene needed for physics movement
      {
        movementType: MovementType.Physics,
        speed: this.MOVE_SPEED,
        jumpForce: this.JUMP_FORCE,
        sprintMultiplier: this.SPRINT_MULTIPLIER,
      },
      playerBody
    );

    this.player = {
      mesh: playerMesh as any,
      bodyId: playerName,
      movement,
      canJump: true,
      isGrounded: false,
      isJumping: false,
      wasInAir: false,
      hasWon: false,
      spaceWasReleased: true,
      positionOffset: positionOffset,
    };
    if (characterModel && (playerMesh as any).animations) {
      this.setupAnimations(playerMesh);
    }
  }
  /** Register idle/walk/run/jump on character; jump uses nextState for automatic transition to idle when clip finishes (like Unity animation tree). */
  private setupAnimations(
    playerMesh: THREE.Mesh | THREE.Group | THREE.Object3D
  ): void {
    const animations = (playerMesh as any).animations as THREE.AnimationClip[];
    if (!animations || animations.length === 0) return;

    // Create AnimationManager
    this.animManager = new AnimationManager(playerMesh, {
      clips: animations
    });

    // Register individual animations
    this.animManager.registerAnimation('idle', {
      name: 'idle',
      loop: THREE.LoopRepeat,
      timeScale: 1.0,
      clampWhenFinished: true
    });

    this.animManager.registerAnimation('walk', {
      name: 'walk',
      loop: THREE.LoopRepeat,
      timeScale: this.WALK_ANIMATION_SCALE,
      clampWhenFinished: true
    });

    this.animManager.registerAnimation('run', {
      name: 'run',
      loop: THREE.LoopRepeat,
      timeScale: this.RUN_ANIMATION_SCALE,
      clampWhenFinished: true
    });

    // Jump animation (we keep jump active until landing to avoid mid-air snapping)
    this.animManager.registerAnimation('jump', {
      name: 'jump',
      loop: THREE.LoopOnce,
      timeScale: 1.0,  // Play at normal speed, transition handled by clip end
      clampWhenFinished: true
    });

    // Start with idle
    if (this.animManager.hasAnimation('idle')) {
      this.animManager.playAnimation('idle', 0);
    }
  }
  /** Resize, keydown (Space/arrows), visibility change; bind resize for cleanup. */
  private setupEventListeners(): void {
    this.boundResizeHandler = this.onWindowResize.bind(this);
    window.addEventListener("resize", this.boundResizeHandler);
    this.canvas.addEventListener("click", () => {
      this.canvas.requestPointerLock();
    });
  }
  /**
   * Create timer UI element (same Bungee box style as score: label + value).
   */
  private createTimer(): void {
    this.timerElement = document.createElement("div");
    this.timerElement.id = "game-timer";
    this.timerElement.className = "score-display timer-display";
    this.timerElement.innerHTML = `
      <span class="score-label">TIME</span>
      <span class="score-value" id="currentTime">01:00</span>
    `;
    this.timerElement.style.display = "none";
    document.body.appendChild(this.timerElement);
  }

  /** Create score display (left) – Bungee box: SCORE only. */
  private createScoreDisplay(): void {
    this.scoreElement = document.createElement("div");
    this.scoreElement.id = "scoreDisplay";
    this.scoreElement.className = "score-display";
    this.scoreElement.innerHTML = `
      <span class="score-label">SCORE</span>
      <span class="score-value" id="currentScore">0 m</span>
    `;
    this.scoreElement.style.display = "none";
    document.body.appendChild(this.scoreElement);
  }


  private updateScoreDisplay(): void {
    if (this.scoreElement && !this.gameEnded && !this.showStartMessage) {
      this.score = Math.max(this.score, Math.floor(this.maxHeightReached));
      const valueEl = document.getElementById("currentScore");
      if (valueEl) valueEl.textContent = `${this.score} m`;
    }
  }

  /**
   * Create start message UI element
   */
  /** Start message overlay; hidden on first input. */
  private createStartMessage(): void {
    this.startMessageElement = document.createElement("div");
    this.startMessageElement.id = "start-message";
    this.startMessageElement.innerHTML = `
      <div class="start-message-card">
        <h1 class="start-message-title">Climb as high as you can!</h1>
      </div>
    `;
    document.body.appendChild(this.startMessageElement);

    // Hide start message and show timer after 2 seconds
    setTimeout(() => {
      if (this.startMessageElement) {
        this.startMessageElement.style.animation = "fadeOut 0.5s ease-out";
        setTimeout(() => {
          if (this.startMessageElement) {
            this.startMessageElement.style.display = "none";
          }
          if (this.timerElement) {
            this.timerElement.style.display = "flex";
            this.timerElement.style.animation = "fadeIn 0.5s ease-in";
          }
          if (this.scoreElement) {
            this.scoreElement.style.display = "flex";
            this.scoreElement.style.animation = "fadeIn 0.5s ease-in";
          }
          this.showStartMessage = false;
        }, 500); // Wait for fade-out animation
      }
    }, 2000); // Show for 2 seconds
  }

  /**
   * Format time as MM:SS or HH:MM:SS
   */
  private formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Update timer display (countdown from 60s)
   */
  private updateTimer(): void {
    if (this.timerElement && !this.gameEnded && !this.showStartMessage) {
      const timeEl = document.getElementById("currentTime");
      if (timeEl) timeEl.textContent = this.formatTime(Math.max(0, this.timeRemaining));
    }
  }

  /**
   * Update atmosphere (lighting, fog, skybox) based on player height
   */
  /** Fog, skybox, light intensity by player height (0 = ground, 1 = top). */
  private updateAtmosphereBasedOnHeight(): void {
    if (!this.player || !this.player.mesh) return;

    const playerHeight = this.player.mesh.position.y;
    const minHeight = 0;
    const maxHeight = 200; // Sliding window for endless mode
    const progress = Math.max(0, Math.min(1, (playerHeight - minHeight) / maxHeight));

    // Dynamic Lighting - brighter at top
    const baseIntensity = 0.7;
    const topIntensity = 1.2;
    const currentIntensity = baseIntensity + (topIntensity - baseIntensity) * progress;
    if (this.hemisphereLight) {
      this.hemisphereLight.intensity = currentIntensity;
    }
    if (this.ambientLight) {
      this.ambientLight.intensity = 0.4 + (0.8 - 0.4) * progress;
    }
    if (this.directionalLight) {
      this.directionalLight.intensity = 1.2 + (2.0 - 1.2) * progress;
    }

    // Fog gradient - denser at lower heights
    const baseFogDensity = 0.012;
    const topFogDensity = 0.003;
    const currentFogDensity = baseFogDensity - (baseFogDensity - topFogDensity) * progress;
    if (this.fog) {
      this.fog.density = currentFogDensity;
    }

    // Skybox color transitions - brighter/more vibrant at top
    if (this.skyboxMesh && this.skyboxMesh.material instanceof THREE.ShaderMaterial) {
      const uniforms = this.skyboxMesh.material.uniforms;
      if (uniforms) {
        const topColorStart = new THREE.Color(this.themeColor("skyboxTopStart"));
        const topColorEnd = new THREE.Color(this.themeColor("skyboxTopEnd"));
        const bottomColorStart = new THREE.Color(this.themeColor("skyboxBottomStart"));
        const bottomColorEnd = new THREE.Color(this.themeColor("skyboxBottomEnd"));
        uniforms.topColor.value.lerpColors(topColorStart, topColorEnd, progress);
        uniforms.bottomColor.value.lerpColors(bottomColorStart, bottomColorEnd, progress);
      }
    }
  }

  /** UIManager + visual joystick (bottom-left, fixed); sync zone with InputManager joystick config. */
  private setupMobileJoystick(): void {
    // Initialize UIManager
    this.uiManager = new UIManager();

    // Create joystick manager
    const joystickManager = this.uiManager.create(UIManager.Type.Joystick);

    // Create visual movement joystick (fixed mode)
    this.moveJoystick = joystickManager.create({
      id: 'movement-visual',
      position: 'bottom-left',
      positionMode: 'fixed',        // Stays at fixed position
      visibilityMode: 'always',     // Always visible
      positionMargin: 100,          // Match InputManager config
      size: 140,
      knobSize: 60,          // Show on desktop for testing
    });
  }
  private onWindowResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };
  /** Cancel animation loop (win or game over). */
  private stopGame(): void {
    if (this.animationId !== undefined) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
  }
  /** Game over overlay; restart button; shows score and reason. */
  private showGameOverScreen(): void {
    const existingScreen = document.getElementById("win-screen") || document.getElementById("gameover-screen");
    if (existingScreen) {
      existingScreen.remove();
    }
    const finalScore = Math.max(this.score, Math.floor(this.maxHeightReached));
    const reason = this.gameOverReason || "Game Over";
    const gameOverDiv = document.createElement("div");
    gameOverDiv.id = "gameover-screen";
    gameOverDiv.innerHTML = `
      <div class="gameover-card">
        <h1 class="gameover-title">Game Over</h1>
        <p class="gameover-reason">${reason}</p>
        <div class="gameover-stats">
          <span class="gameover-stat"><span class="gameover-stat-label">Score</span>${finalScore} m</span>
        </div>
        <button id="restart-button-gameover" class="gameover-btn">Try Again</button>
      </div>
    `;
    document.body.appendChild(gameOverDiv);

    // Add restart button event listeners (click and touch for mobile)
    const restartButton = document.getElementById("restart-button-gameover");
    if (restartButton) {
      const handleRestart = () => {
        this.restartGame();
      };
      restartButton.addEventListener("click", handleRestart);
      restartButton.addEventListener("touchend", (e) => {
        e.preventDefault();
        handleRestart();
      }, { passive: false });
    }
  }

  /** Reset game state, player position, counters; remove overlays; restart loop. */
  private restartGame(): void {
    // Remove win/game over screens
    const winScreen = document.getElementById("win-screen");
    const gameOverScreen = document.getElementById("gameover-screen");
    if (winScreen) winScreen.remove();
    if (gameOverScreen) gameOverScreen.remove();

    // Reset game state
    this.gameEnded = false;
    this.gameOverReason = "";
    this.time = 0;
    this.lastTime = 0;
    this.timeRemaining = Game.TIMER_START;
    this.score = 0;
    this.maxHeightReached = 0;
    this.showStartMessage = true;
    this.player.canJump = true;
    this.player.isGrounded = false;
    this.player.isJumping = false;
    this.player.wasInAir = false;
    this.player.spaceWasReleased = true;

    // Reset player position
    const body = this.physicsHandler.getBody(this.worldId, this.player.bodyId);
    if (body) {
      this.physicsHandler.setBodyPosition(
        this.worldId,
        this.player.bodyId,
        [0, 2, 0]
      );
      body.velocity.set(0, 0, 0);
    }

    // Clear all platforms: remove every platform body and mesh (by id/name) so retry resets fully
    const allBodyIds = this.physicsHandler.getBodyIds(this.worldId);
    for (const bodyId of allBodyIds) {
      if (bodyId.startsWith("platform-")) {
        this.physicsHandler.deleteBody(this.worldId, bodyId, false);
      }
    }
    const toRemove: THREE.Object3D[] = [];
    this.scene.traverse((obj) => {
      if (obj.name && obj.name.startsWith("platform-")) {
        toRemove.push(obj);
      }
    });
    toRemove.forEach((obj) => this.scene.remove(obj));
    this.platforms = [];
    this.nextPlatformIndex = 0;
    this.generateSpiralPlatforms();

    // Sync platform meshes with physics bodies so layout matches first load
    this.platforms.forEach((platform) => {
      this.physicsHandler.syncMeshWithBody(
        this.worldId,
        platform.bodyId,
        platform.mesh as any
      );
    });
    // Reset time base so first frame after restart uses a small delta (like first load)
    this.lastTime = performance.now() / 1000;

    // Show start message and hide timer
    if (this.startMessageElement) {
      this.startMessageElement.style.display = "block";
      this.startMessageElement.style.animation = "fadeIn 0.5s ease-in";
    }
    if (this.timerElement) {
      this.timerElement.style.display = "none";
      const timeEl = document.getElementById("currentTime");
      if (timeEl) timeEl.textContent = "01:00";
    }
    if (this.scoreElement) {
      this.scoreElement.style.display = "none";
      const valueEl = document.getElementById("currentScore");
      if (valueEl) valueEl.textContent = "0 m";
    }

    // Hide start message and show timer after 2 seconds
    setTimeout(() => {
      if (this.startMessageElement) {
        this.startMessageElement.style.animation = "fadeOut 0.5s ease-out";
        setTimeout(() => {
          if (this.startMessageElement) {
            this.startMessageElement.style.display = "none";
          }
          if (this.timerElement) {
            this.timerElement.style.display = "flex";
            this.timerElement.style.animation = "fadeIn 0.5s ease-in";
          }
          if (this.scoreElement) {
            this.scoreElement.style.display = "flex";
            this.scoreElement.style.animation = "fadeIn 0.5s ease-in";
          }
          this.showStartMessage = false;
        }, 500);
      }
    }, 2000);

    // Resume animation if it was stopped
    if (this.animationId === undefined) {
      this.animate();
    }
  }
  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    if (this.gameEnded) {
      this.renderer.render(this.scene, this.camera);
      return;
    }

    // Calculate deltaTime dynamically (like in physics-demo)
    const currentTime = performance.now() / 1000;
    const cappedDelta = Math.min(
      this.lastTime > 0 ? currentTime - this.lastTime : 0.016,
      0.1
    );
    this.lastTime = currentTime;

    if (!this.showStartMessage) {
      this.time += cappedDelta;
      this.timeRemaining -= cappedDelta;
      if (this.timeRemaining <= 0) {
        this.timeRemaining = 0;
        this.gameOverReason = "Time's up!";
        this.gameEnded = true;
        this.stopGame();
        this.showGameOverScreen();
      }
    }
    this.updateTimer();
    this.updateScoreDisplay();
    this.inputHandler.update(cappedDelta);

    // Unlimited platforms: spawn above when player gets close to top, remove far below
    const playerY = this.player?.mesh?.position.y ?? 0;
    const highestY = this.getHighestPlatformY();
    if (playerY > highestY - Game.PLATFORM_SPAWN_AHEAD) {
      this.spawnMorePlatforms(20);
    }
    this.removePlatformsBelowY(playerY - Game.PLATFORM_REMOVE_BELOW);

    this.platforms.forEach((platform) => {
      if (platform.isMoving) {
        platform.previousPosition = [
          platform.mesh.position.x,
          platform.mesh.position.y,
          platform.mesh.position.z,
        ];
        const oscillation =
          Math.sin(
            this.time * platform.movementSpeed + platform.movementPhase
          ) * platform.movementAmplitude;
        const newPosition: [number, number, number] = [
          platform.originalPosition[0] +
          platform.movementDirection[0] * oscillation,
          platform.originalPosition[1] +
          platform.movementDirection[1] * oscillation,
          platform.originalPosition[2] +
          platform.movementDirection[2] * oscillation,
        ];
        this.physicsHandler.setBodyPosition(
          this.worldId,
          platform.bodyId,
          newPosition
        );
      }
    });
    this.updatePlayer(cappedDelta);
    // Always advance animation mixer every frame so animations never get stuck (e.g. when no input or early return)
    if (this.animManager) this.animManager.update(cappedDelta);
    this.physicsWorld.step(cappedDelta);
    this.platforms.forEach((platform) => {
      this.physicsHandler.syncMeshWithBody(
        this.worldId,
        platform.bodyId,
        platform.mesh as any
      );
    });
    // Sync only position for floor, not rotation (mesh rotation is set manually)
    this.physicsHandler.syncMeshWithBody(
      this.worldId,
      "floor",
      this.floorMesh as any,
      {
        syncPosition: true,
        syncRotation: false, // Don't sync rotation - mesh rotation is set manually
      }
    );
    // Sync mesh with physics body - physics body center should match bounding box center
    // Use stored position offset to maintain alignment
    this.physicsHandler.syncMeshWithBody(
      this.worldId,
      this.player.bodyId,
      this.player.mesh as any,
      {
        syncPosition: true,
        syncRotation: false,
        positionOffset: this.player.positionOffset,
      }
    );

    this.cameraManager.update(cappedDelta);

    // Update dynamic lighting, fog, and skybox based on player height
    this.updateAtmosphereBasedOnHeight();

    // Update visual joystick to match input state
    if (this.moveJoystick) {
      const moveState = this.inputHandler.getJoystick('movement');
      if (moveState) {
        this.moveJoystick.update(
          moveState.vector,
          moveState.isActive,
          moveState.centerPosition
        );
      }
    }

    this.renderer.render(this.scene, this.camera);
  };
  /** Per-frame: input.update(); movement.update(); sync mesh to body; grounded/jump state; animation by state; win/game-over checks. */
  private updatePlayer(_deltaTime: number): void {
    if (this.gameEnded) {
      return;
    }

    // Don't allow player movement until start message disappears
    if (this.showStartMessage) {
      return;
    }

    const body = this.physicsHandler.getBody(this.worldId, this.player.bodyId);
    if (!body) return;

    // Get bounding box height for accurate raycast distance
    this.player.mesh.updateMatrixWorld(true);
    const bbox = new THREE.Box3();
    bbox.setFromObject(this.player.mesh);
    const bboxSize = bbox.getSize(new THREE.Vector3());
    const bboxHeight = bboxSize.y;

    const playerRadius = 0.5;
    const castRadius = playerRadius * 0.5; // Decreased from 0.8 to 0.5 for smaller detection area
    const groundCheck = this.physicsHandler.checkGroundSphereCast(
      this.worldId,
      this.player.bodyId,
      castRadius,
      undefined, // Let the method calculate from boundingBoxHeight
      0,
      bboxHeight // Pass bounding box height
    );
    this.player.isGrounded = groundCheck ? groundCheck.isGrounded : false;

    // Track if player was in air (for landing detection)
    if (!this.player.isGrounded) {
      this.player.wasInAir = true;
    }

    // Detect landing to smooth transitions after jump/fall
    let justLanded = false;
    if (this.player.isGrounded && this.player.wasInAir) {
      justLanded = true;
      this.player.wasInAir = false;
      this.player.isJumping = false;
    }

    if (this.player.isGrounded && !this.player.canJump) this.player.canJump = true;

    // Score = max height reached (integer meters)
    const playerY = body.position.y;
    if (playerY > this.maxHeightReached) {
      this.maxHeightReached = playerY;
    }

    // Get input from keyboard/joystick
    let inputVector = this.inputHandler.getMovementVector();

    // Check for joystick input (overrides keyboard if active)
    const joystickVector = this.inputHandler.getJoystickVector('movement');
    if (this.inputHandler.isJoystickActive('movement')) {
      inputVector = { x: joystickVector.x, y: joystickVector.y };
    }

    const isSprinting = this.inputHandler.isKeyDown(KeyCode.SHIFT_LEFT);
    const spaceReleased = this.inputHandler.isKeyUp(KeyCode.SPACE);
    if (spaceReleased) this.player.spaceWasReleased = true;
    const isMoving = inputVector.x !== 0 || inputVector.y !== 0;
    const cameraYaw = this.cameraManager.getYaw();
    // MovementManager handles physics movement automatically
    this.player.movement.update(
      inputVector,
      isSprinting,
      1,
      undefined,
      cameraYaw
    );

    if (!isMoving) {
      body.velocity.x *= 0.8;
      body.velocity.z *= 0.8;
    }
    if (this.player.isGrounded) {
      const playerPos = this.player.mesh.position;
      const groundedPlatform = this.platforms.find((p) => {
        const platformTopY = p.mesh.position.y + 0.25;
        const horizontalDistance = Math.sqrt(
          Math.pow(playerPos.x - p.mesh.position.x, 2) +
          Math.pow(playerPos.z - p.mesh.position.z, 2)
        );
        const verticalDistance = Math.abs(playerPos.y - platformTopY);
        const platformHalfSize = p.size / 2;
        const toleranceMargin = 0.5;
        return (
          verticalDistance < 1.5 &&
          horizontalDistance < platformHalfSize + toleranceMargin
        );
      });

      if (groundedPlatform && groundedPlatform.isMoving) {
        this.physicsHandler.syncKinematicPlatformRider(
          this.worldId,
          groundedPlatform.bodyId,
          groundedPlatform.mesh as any,
          this.player.bodyId,
          groundedPlatform.previousPosition
        );
      }
    }
    // Check for distance-based tap or keyboard jump
    const isTapJump = this.inputHandler.isTap(); // Uses InputModule's distance-based tap detection
    const isKeyboardJump = this.inputHandler.isKeyDown(KeyCode.SPACE);

    // Tap can jump immediately (distance-based), keyboard requires spaceWasReleased to prevent holding
    const canJumpNow = (isTapJump || (isKeyboardJump && this.player.spaceWasReleased));

    if (canJumpNow && this.player.isGrounded && this.player.canJump) {
      const jumped = this.player.movement.jump();
      if (jumped) {
        this.player.canJump = false;
        this.player.isJumping = true;
        this.player.wasInAir = false; // Reset to track this jump cycle
        if (isKeyboardJump) {
          this.player.spaceWasReleased = false;
        }
        // Play jump animation immediately when jump is triggered (not every frame)
        // The animation will auto-transition to idle when the clip finishes via nextState config
        if (this.animManager && this.animManager.hasAnimation('jump')) {
          // Cut current (idle/walk/run) to avoid mixing with jump
          this.animManager.playAnimation('jump', 0.15, { cutCurrent: true });
        }
      }
    }

    // Update animations based on movement state
    // Animation transitions for jump->idle are handled automatically by AnimationManager via nextState config
    // We only handle walk/run/idle transitions here - jump is triggered at jump time above
    if (this.animManager) {
      // Don't interfere with jump animation - let it play and auto-transition to idle
      // Only manage walk/run/idle animations
      if (!this.player.isJumping) {
        const targetState: 'idle' | 'walk' | 'run' = isMoving ? (isSprinting ? 'run' : 'walk') : 'idle';

        // Only switch animation if state changed AND the animation exists
        if (this.animManager.hasAnimation(targetState)) {
          const currentState = this.animManager.getCurrentState();
          if (currentState !== targetState) {
            const transitionDuration =
              justLanded || currentState === 'jump' ? 0.25 : 0.2;
            this.animManager.playAnimation(targetState, transitionDuration);
          }
        } else {
          // If target animation doesn't exist, stop current animation to stay in static position
          if (this.animManager.getCurrentState()) {
            this.animManager.stopAnimation(0.2);
          }
        }
      }
    }
    // Mixer is updated every frame in animate() so it never gets stuck
    // Removed Y < -50 check - now using ground touch counting instead
  }
  /** RequestAnimationFrame loop: deltaTime, updatePlayer(deltaTime), move platforms, camera.update(), render. */
  private start(): void {
    this.renderer.render(this.scene, this.camera);
    this.animate();
  }
  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    window.removeEventListener("resize", this.boundResizeHandler);
    if (this.timerElement) {
      this.timerElement.remove();
    }
    if (this.scoreElement) {
      this.scoreElement.remove();
    }
    if (this.startMessageElement) {
      this.startMessageElement.remove();
    }
    if (this.uiManager) {
      this.uiManager.dispose();
    }
    this.renderer.dispose();
  }
}
new Game();
